import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { RiCloseLargeLine } from 'react-icons/ri'
import { ModalBody, ModalBox, ModalFooter, ModalHeader, ModalOverlay } from './styles'

const FOCUSABLE = 'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled)'

// Modal acessível: role=dialog, foco inicial e preso dentro do diálogo, Esc e clique no fundo fecham,
// e só o corpo rola (o cabeçalho e o rodapé continuam visíveis no celular).
export function Modal({ title, onClose, footer, size, children }) {
  const boxRef = useRef(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    const previouslyFocused = document.activeElement
    const previousOverflow = document.body.style.overflow
    const box = boxRef.current

    document.body.style.overflow = 'hidden'
    // Foca o primeiro campo de formulário; sem ele, o próprio diálogo.
    ;(box.querySelector('input, select, textarea') || box).focus()

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onCloseRef.current()
        return
      }

      if (event.key !== 'Tab') return

      const items = [...box.querySelectorAll(FOCUSABLE)].filter((item) => item.offsetParent !== null)

      if (items.length === 0) {
        event.preventDefault()
        return
      }

      const first = items[0]
      const last = items[items.length - 1]

      if (event.shiftKey && (document.activeElement === first || document.activeElement === box)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') previouslyFocused.focus()
    }
  }, [])

  return createPortal(
    <ModalOverlay
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <ModalBox ref={boxRef} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} $size={size}>
        <ModalHeader>
          <h2>{title}</h2>
          <button type="button" aria-label="Fechar" title="Fechar" onClick={onClose}>
            <RiCloseLargeLine />
          </button>
        </ModalHeader>
        <ModalBody>{children}</ModalBody>
        {footer && <ModalFooter>{footer}</ModalFooter>}
      </ModalBox>
    </ModalOverlay>,
    document.body
  )
}
